// angular
import { Injectable } from "@angular/core";
// multi-asset
import * as CubiqTS from "shared/cubiq-model-multi-asset";
// global-services

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

// order-entry services
import { OEUtilityService } from "./basic/oe-utility.service";
import { OEModelGetterService } from "./model/oe-model-getter.service";
import { OEModelSetterService } from "./model/oe-model-setter.service";
// order-entry models
import { OEUpdateEventModel } from "../data/models/oe-update-event.model";
// order-entry enums
import { OENDisplayState } from "../data/enums/oe-display-state.enum";
import { OENCommand } from "../data/enums/oe-command.enum";
///

@Injectable()
export class OESyncService {
  /**********************************************************************/
  /* DATA */
  private control: any = {
    forwardRegistry: new Map<string, string>(), // <forwardRefId, receiverRefId>
    blockUpdateForRefIds: [],
  };

  /**********************************************************************/
  /* CONSTRUCTOR */
  constructor(
    private utility: OEUtilityService,
    private getter: OEModelGetterService,
    private setter: OEModelSetterService,
  ) {}

  /**********************************************************************/
  /* FORWARD REGISTRY */
  /*  */
  public registerForward(forwardRefId: string, receiverRefId: string): void {
    if (!this.control.forwardRegistry.has(forwardRefId)) {
      this.control.forwardRegistry.set(forwardRefId, receiverRefId);
      logger.out.info(
        `BFX-DEV: _oeWorker: RefId forward established ${forwardRefId} => ${receiverRefId}`,
      );
    } else {
      logger.out.error(
        `ERROR: RefId ${forwardRefId} already in registry! Stored forward '${this.control.forwardRegistry.get(
          forwardRefId,
        )}', new forward '${receiverRefId}'`,
      );
    }
  }

  public unregisterForward(forwardRefId: string): void {
    if (this.control.forwardRegistry.has(forwardRefId)) {
      this.control.forwardRegistry.delete(forwardRefId);
    } else {
      logger.out.error(`ERROR: RefId ${forwardRefId} NOT in registry! `);
    }
  }

  public isTheRefIdForwarded(forwardRefId: string): boolean {
    return this.control.forwardRegistry.has(forwardRefId);
  }

  public getForwardTargetRefId(forwardRefId: string): string {
    if (this.control.forwardRegistry.has(forwardRefId)) {
      return this.control.forwardRegistry.get(forwardRefId);
    }
    return null;
  }

  /**********************************************************************/
  /* UPDATE EVENTS QUEUE and Management */
  public addEventUpdateToQueue(
    refId: string,
    updateEventModel: OEUpdateEventModel,
    addEventAnytime: boolean = false,
  ): void {
    if (this.getter.has(refId) && updateEventModel != null) {
      if (this.getter.get(refId).eventOrderStateUpdateQueue == null) {
        this.getter.get(refId).eventOrderStateUpdateQueue = new Array<OEUpdateEventModel>();
      }
      if (updateEventModel == null) {
        // just a warning, not a guard atm
        logger.out.warn(`WARN: _oeMode.addEventToStateUpdateQueue(): Event state is 'null'`);
      }
      if (addEventAnytime || this.getter.isInInitialization(refId)) {
        logger.out.debug(`DEBUG: _oeModel: Update event  added to queue! %O`, updateEventModel);
        this.getter.get(refId).eventOrderStateUpdateQueue.push(updateEventModel);
      }
    }
  }

  public peekLatestEventUpdate(refId: string): OEUpdateEventModel {
    if (
      this.getter.has(refId) &&
      this.getter.get(refId).eventOrderStateUpdateQueue != null &&
      this.getter.get(refId).eventOrderStateUpdateQueue.length > 0
    ) {
      let queue = this.getter.get(refId).eventOrderStateUpdateQueue;
      let updateEvent = queue[queue.length - 1];
      return updateEvent;
    }
    return null;
  }

  public popLatestEventUpdateAndClearQueue(refId: string): OEUpdateEventModel {
    if (
      this.getter.has(refId) &&
      this.getter.get(refId).eventOrderStateUpdateQueue != null &&
      this.getter.get(refId).eventOrderStateUpdateQueue.length > 0
    ) {
      let latestEvent = this.getter.get(refId).eventOrderStateUpdateQueue.pop();
      this.getter.get(refId).eventOrderStateUpdateQueue = [];
      return latestEvent;
    }
    return null;
  }

  public areThereUpdateEvents(refId: string): boolean {
    return (
      this.getter.has(refId) &&
      this.getter.get(refId).eventOrderStateUpdateQueue != null &&
      this.getter.get(refId).eventOrderStateUpdateQueue.length > 0
    );
  }

  public resetEventUpdateQueue(refId: string): void {
    if (this.getter.has(refId)) {
      this.getter.get(refId).eventOrderStateUpdateQueue = null;
    }
  }

  public compareThisWithLatestUpdateEvent(refId: string): boolean {
    if (this.getter.has(refId)) {
      if (this.areThereUpdateEvents(refId)) {
        let updateEvent = this.peekLatestEventUpdate(refId);
        let displayState = this.latestEventUpdate_getDisplayState(refId);
        let model = this.getter.get(refId);
        logger.out.debug(`DEBUG: _oeModel: Compare (M)odel to latest (EV)entUpdate => 
								state: M[${CubiqTS.OrderState[model.srvOrderState]}] ${
          model.srvOrderState === updateEvent.orderState ? "===" : "!==="
        } EV[${CubiqTS.OrderState[updateEvent.orderState]}] |
								displayState: M[${OENDisplayState[model.displayState]}] ${
          displayState == null || model.displayState === displayState ? "===" : "!==="
        } EV[${displayState != null ? OENDisplayState[displayState] : "ANY"}] |
								lockedForMod: M[${model.isLockedForModification ? "Yes" : "No"}] ${
          updateEvent.lockedForMod == null ||
          model.isLockedForModification === updateEvent.lockedForMod
            ? "==="
            : "!==="
        } EV[${
          updateEvent.lockedForMod != null ? (updateEvent.lockedForMod ? "Yes" : "No") : "ANY"
        }] | RESULT => 
								${
                  model.srvOrderState === updateEvent.orderState &&
                  (displayState == null || model.displayState === displayState) &&
                  (updateEvent.lockedForMod == null ||
                    model.isLockedForModification === updateEvent.lockedForMod)
                    ? "MATCH !"
                    : "NO MATCH !"
                }`);
        return (
          model.srvOrderState === updateEvent.orderState &&
          (updateEvent.cmdStateChange == null ||
            model.displayState ===
              this.utility.transformCmdToDisplayState(updateEvent.cmdStateChange)) &&
          (updateEvent.lockedForMod == null ||
            model.isLockedForModification === updateEvent.lockedForMod)
        );
      } else {
        // no update events
        logger.out.debug(`DEBUG: _oeModel.compareThisWithLatestUpdateEvent(): No update events!`);
        return true;
      }
    } else {
      throw new Error(`Comparison failed! Model ref: '${refId}' not found.`);
    }
  }

  public isLatestEventModifyStateEvent(refId: string): boolean {
    if (this.getter.has(refId)) {
      if (this.areThereUpdateEvents(refId)) {
        let cmdState = this.latestEventUpdate_getCmd(refId);
        return (
          cmdState != null &&
          (cmdState === OENCommand.STATE_ORDER_IN_MODIFICATION ||
            cmdState === OENCommand.STATE_ORDER_MODIFY_OUT_ACCEPTANCE ||
            cmdState === OENCommand.STATE_ORDER_EXT_MODIFY_OUT_ACCEPTANCE ||
            cmdState === OENCommand.STATE_ORDER_UNLOCKED ||
            cmdState === OENCommand.STATE_ORDER_REJECTED ||
            cmdState === OENCommand.STATE_ORDER_REJECTED_HCO_ERROR ||
            cmdState === OENCommand.STATE_ORDER_APPROVAL_ALLOWED ||
            cmdState === OENCommand.STATE_ORDER_APPROVAL_INTERNAL ||
            cmdState === OENCommand.STATE_ORDER_APPROVAL_EXTERNAL ||
            cmdState === OENCommand.STATE_ORDER_ACCEPTANCE_ALLOWED ||
            cmdState === OENCommand.STATE_ORDER_ACCEPTANCE_EXTERNAL ||
            cmdState === OENCommand.STATE_ORDER_ACCEPTANCE_CALL)
        );
      }
    }
  }

  public applyLatestUpdateEvent(
    refId: string,
    applyToAll: boolean = true,
    isModifyExpected: boolean = true,
    resetQueue: boolean = false,
  ): boolean {
    if (this.getter.has(refId)) {
      if (applyToAll && this.getter.hasStrategy(refId)) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        let result: boolean = false;
        propagationTree.forEach((itemRefId: string) => {
          result =
            result || this.executeApplicationOfUpdateEvent(itemRefId, isModifyExpected, resetQueue);
        });
        return result;
      } else {
        return this.executeApplicationOfUpdateEvent(refId, isModifyExpected, resetQueue);
      }
    }
  }

  private executeApplicationOfUpdateEvent(
    refId: string,
    isModifyExpected: boolean,
    resetQueue: boolean,
  ): boolean {
    if (this.getter.has(refId) && this.areThereUpdateEvents(refId)) {
      let result: boolean = false;
      if (!isModifyExpected || (isModifyExpected && this.isLatestEventModifyStateEvent(refId))) {
        if (!this.compareThisWithLatestUpdateEvent(refId)) {
          logger.out.debug(
            `DEBUG: _oeModel.executeApplicationOfUpdateEvent(): Event update APPLIED!`,
          );
          let updateEvent = this.popLatestEventUpdateAndClearQueue(refId);
          this.setter.setSrvOrderState(refId, updateEvent.orderState);
          updateEvent.lockedForMod
            ? this.setter.setLockedForModification(refId, updateEvent.lockUserId)
            : this.setter.removeLockForModification(refId);
          if (updateEvent.cmdStateChange != null) {
            this.setter.setOrderDisplayState(
              refId,
              this.utility.transformCmdToDisplayState(updateEvent.cmdStateChange),
            );
          }
          result = true;
        } else {
          logger.out.debug(
            `DEBUG: _oeModel.executeApplicationOfUpdateEvent(): Event update SKIPPED!`,
          );
          result = false;
        }
      } else {
        logger.out.debug(
          `DEBUG: _oeModel..executeApplicationOfUpdateEvent(): Event update SKIPPED (not a valid MODIFY state!)`,
        );
        result = false;
      }
      if (resetQueue) {
        this.resetEventUpdateQueue(refId);
      }
      return result;
    }
  }

  public latestEventUpdate_getData(refId: string): any {
    if (this.getter.has(refId) && this.areThereUpdateEvents(refId)) {
      let updateEvent = this.peekLatestEventUpdate(refId);
      return updateEvent != null ? updateEvent.data : null;
    }
    return null;
  }
  public latestEventUpdate_getDisplayState(refId: string): OENDisplayState {
    let cmd = this.latestEventUpdate_getCmd(refId);
    return cmd != null ? this.utility.transformCmdToDisplayState(cmd) : null;
  }

  public latestEventUpdate_getCmd(refId: string): OENCommand {
    if (this.getter.has(refId) && this.areThereUpdateEvents(refId)) {
      let updateEvent = this.peekLatestEventUpdate(refId);
      return updateEvent != null ? updateEvent.cmdStateChange : null;
    }
    return null;
  }
  public latestEventUpdate_getLocked(refId: string): boolean {
    if (this.getter.has(refId) && this.areThereUpdateEvents(refId)) {
      let updateEvent = this.peekLatestEventUpdate(refId);
      return updateEvent != null ? updateEvent.lockedForMod : null;
    }
    return null;
  }

  public latestEventUpdate_getSubscriptionLifeCycleActive(refId: string): boolean {
    if (this.getter.has(refId) && this.areThereUpdateEvents(refId)) {
      let updateEvent = this.peekLatestEventUpdate(refId);
      return updateEvent != null ? updateEvent.subscriptionLifeCycleActive : null;
    }
    return null;
  }

  public latestEventUpdate_getSubscriptionInterventionActive(refId: string): boolean {
    if (this.getter.has(refId) && this.areThereUpdateEvents(refId)) {
      let updateEvent = this.peekLatestEventUpdate(refId);
      return updateEvent != null ? updateEvent.subscriptionInterventionActive : null;
    }
    return null;
  }

  public latestEventUpdate_getLockActions(refId: string): boolean {
    if (this.getter.has(refId) && this.areThereUpdateEvents(refId)) {
      let updateEvent = this.peekLatestEventUpdate(refId);
      return updateEvent != null ? updateEvent.lockActions : null;
    }
    return null;
  }

  public latestEventUpdate_getLockInterface(refId: string): boolean {
    if (this.getter.has(refId) && this.areThereUpdateEvents(refId)) {
      let updateEvent = this.peekLatestEventUpdate(refId);
      return updateEvent != null ? updateEvent.lockInterface : null;
    }
    return null;
  }
}
